import Icon from "@components/Atoms/Icon";
import Tooltip from "@components/Atoms/Tooltip";
import HotKey from "@components/GetHotKey";
import { classNames } from "@components/libs/classNames";
import ButtonStateService from "@core-ui/ContextServices/ButtonStateService/ButtonStateService";
import type { NodeValues } from "@core-ui/ContextServices/ButtonStateService/hooks/types";
// biome-ignore lint/style/noRestrictedImports: pre-existing @emotion/styled import; the Tailwind migration is not this change's scope
import styled from "@emotion/styled";
import t from "@ext/localization/locale/translate";
import { type CSSProperties, type ForwardedRef, forwardRef, type HTMLAttributes } from "react";

export interface ButtonProps extends HTMLAttributes<HTMLDivElement> {
	isActive?: boolean;
	tooltipText?: string;
	disabled?: boolean;
	hotKey?: string;
	title?: string;
	nodeValues?: NodeValues;
	icon?: string;
	iconStyle?: CSSProperties;
	iconStrokeWidth?: string;
	iconViewBox?: string;
	dataQa?: string;
	onMouseLeave?: () => void;
	useSvgDefaultWidth?: boolean;
	text?: string;
}

const Button = forwardRef((props: ButtonProps, ref: ForwardedRef<HTMLDivElement>) => {
	const { iconStyle = { fontWeight: 300 }, iconViewBox, iconStrokeWidth, icon, ...propsWithoutIcon } = props;

	const {
		isActive: propsIsActive = false,
		disabled: propsDisabled = false,
		onMouseLeave,
		onClick,
		tooltipText,
		nodeValues,
		dataQa,
		children,
		hotKey,
		text,
		...otherProps
	} = propsWithoutIcon;

	const { disabled, isActive } = nodeValues
		? ButtonStateService.useCurrentAction(nodeValues)
		: { disabled: propsDisabled, isActive: propsIsActive };

	const mods = {
		disabled: disabled,
		"is-active": isActive,
	};

	const ButtonContent = (
		<div className={classNames("button", mods)} onClick={disabled ? null : onClick} onMouseLeave={onMouseLeave}>
			{icon ? (
				<div className="iconFrame">
					<Icon code={icon} strokeWidth={iconStrokeWidth} style={iconStyle} viewBox={iconViewBox} />
					{text && <span>{text}</span>}
				</div>
			) : (
				children
			)}
		</div>
	);

	const ButtonWithTooltip = () => (
		<Tooltip content={<ButtonTooltipContent disabled={disabled} hotKey={hotKey} tooltipText={tooltipText} />}>
			{ButtonContent}
		</Tooltip>
	);

	return (
		<div data-qa="qa-edit-menu-button">
			<StyledDiv data-qa={dataQa} ref={ref} {...otherProps}>
				{tooltipText ? ButtonWithTooltip() : ButtonContent}
			</StyledDiv>
		</div>
	);
});

const ButtonTooltipContent = styled(
	(props: { tooltipText?: string; hotKey?: string; disabled?: boolean; className?: string }) => {
		const { tooltipText, hotKey, disabled, className } = props;

		return (
			<div className={className}>
				<div className="action">
					{tooltipText}
					{hotKey && <HotKey hotKey={hotKey} />}
				</div>
				{/* The button says what it does; when it is off, it has to say why. */}
				{disabled && <div className="reason">{t("disabled-reason.editor-not-applicable")}</div>}
			</div>
		);
	},
)`
	gap: 4px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;

	.action {
		gap: 8px;
		display: flex;
		align-items: center;
	}

	.reason {
		opacity: 0.75;
	}

	span.cmd {
		border-radius: var(--radius-x-small);
		color: var(--color-article-text);
		background: var(--color-article-bg) !important;
	}
`;

const StyledDiv = styled.div<ButtonProps>`
	.iconFrame {
		display: flex;
		align-items: center;
		line-height: 100%;
		padding: 6.5px 7px;

		i {
			font-size: 12.5px;
		}
	}

	.button {
		cursor: pointer;
		font-size: 12px;
		border-radius: var(--radius-small);
	}

	.button:hover:not(.disabled),
	.button.is-active {
		background: var(--color-edit-menu-button-active-bg);
	}

	.button.disabled {
		cursor: default;
		opacity: 0.4 !important;
	}

	i.fa-fw {
		margin-left: 0 !important;
	}
`;

export default Button;
